import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useWatch, useController } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { freightSchema, type FreightFormData } from "@/validators/freight";
import {
  useFreight,
  useCreateFreight,
  useUpdateFreight,
} from "@/hooks/useFreights";
import { useCompanies } from "@/hooks/useCompanies";
import { useDrivers } from "@/hooks/useDrivers";
import { useVehicles } from "@/hooks/useVehicles";
import { FormSection } from "@/components/form/form-section";
import { MoneyInput } from "@/components/form/money-input";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";

const UF_LIST = [
  "AC",
  "AL",
  "AM",
  "AP",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MG",
  "MS",
  "MT",
  "PA",
  "PB",
  "PE",
  "PI",
  "PR",
  "RJ",
  "RN",
  "RO",
  "RR",
  "RS",
  "SC",
  "SE",
  "SP",
  "TO",
];

const selectClass = cn(
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

function MarginDisplay({
  freightValue,
  driverValue,
}: {
  freightValue: number | "";
  driverValue: number | "";
}) {
  const fv =
    typeof freightValue === "number" && freightValue > 0 ? freightValue : null;
  const dv =
    typeof driverValue === "number" && driverValue > 0 ? driverValue : null;

  if (!fv) return null;

  const margin = fv - (dv ?? 0);
  const pct = ((margin / fv) * 100).toFixed(1);
  const fmt = (v: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(v);

  return (
    <div
      className={cn(
        "rounded-md border px-4 py-3 text-sm",
        margin >= 0
          ? "border-green-200 bg-green-50"
          : "border-red-200 bg-red-50",
      )}
    >
      <p className="text-xs font-medium text-muted-foreground mb-1">
        Margem estimada
      </p>
      <p
        className={cn(
          "font-semibold",
          margin >= 0 ? "text-green-700" : "text-red-600",
        )}
      >
        {fmt(margin)} <span className="font-normal text-xs">({pct}%)</span>
      </p>
    </div>
  );
}

export function FreightFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { has } = usePermissions();
  const isOperator = !has("freight.set_value");

  const { data: freight, isLoading: loadingFreight } = useFreight(id ?? "");
  const createMutation = useCreateFreight();
  const updateMutation = useUpdateFreight(id ?? "");
  const mutation = isEdit ? updateMutation : createMutation;

  useEffect(() => {
    if (isEdit && freight && freight.status !== "PENDING") {
      toast.info(
        "Apenas fretes pendentes podem ser editados aqui. Use 'Editar valores' para ajustar valores.",
      );
      navigate(`/freights/${freight.id}`, { replace: true });
    }
  }, [isEdit, freight, navigate]);

  const { data: companiesData } = useCompanies({ active: true, perPage: 200 });
  const { data: driversData } = useDrivers({ active: true, perPage: 200 });
  const { data: vehiclesData } = useVehicles({ active: true, perPage: 200 });

  const companyOptions = (companiesData?.data ?? []).map((c) => ({
    value: c.id,
    label: c.tradeName,
  }));
  const driverOptions = (driversData?.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));
  const vehicleOptions = (vehiclesData?.data ?? []).map((v) => ({
    value: v.id,
    label: `${v.plate}${v.brand ? ` — ${v.brand}` : ""}${v.model ? ` ${v.model}` : ""}`,
  }));

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FreightFormData>({
    resolver: zodResolver(freightSchema),
    defaultValues: {
      companyId: "",
      driverId: "",
      vehicleId: "",
      originCity: "",
      originState: "",
      destinationCity: "",
      destinationState: "",
      cargoDescription: "",
      cargoWeight: "" as unknown as number,
      cargoType: "",
      cteNumber: "",
      deliveryNfeNumber: "",
      freightValue: "" as unknown as number,
      driverValue: "" as unknown as number,
      advancePercent: 70,
      notes: "",
    },
  });

  const {
    field: companyField,
    fieldState: { error: companyError },
  } = useController({ control, name: "companyId" });
  const { field: driverField } = useController({ control, name: "driverId" });
  const { field: vehicleField } = useController({ control, name: "vehicleId" });

  const freightValue = useWatch({ control, name: "freightValue" });
  const driverValue = useWatch({ control, name: "driverValue" });

  // Sugere valor do motorista (70% do frete) enquanto o usuário não tiver editado manualmente
  useEffect(() => {
    if (isEdit) return;
    if (dirtyFields.driverValue) return;
    if (typeof freightValue !== "number" || freightValue <= 0) return;
    const suggested = Math.round(freightValue * 70) / 100; // 70%, 2 casas
    setValue("driverValue", suggested as unknown as number);
  }, [freightValue, dirtyFields.driverValue, isEdit, setValue]);

  useEffect(() => {
    if (freight) {
      reset({
        companyId: freight.companyId,
        driverId: freight.driverId ?? "",
        vehicleId: freight.vehicleId ?? "",
        originCity: freight.originCity,
        originState: freight.originState,
        destinationCity: freight.destinationCity,
        destinationState: freight.destinationState,
        cargoDescription: freight.cargoDescription,
        cargoWeight: (freight.cargoWeight
          ? parseFloat(freight.cargoWeight)
          : "") as unknown as number,
        cargoType: freight.cargoType ?? "",
        cteNumber: freight.cteNumber ?? "",
        deliveryNfeNumber: freight.deliveryNfeNumber ?? "",
        freightValue: (freight.freightValue ? parseFloat(freight.freightValue) : "") as unknown as number,
        driverValue: (freight.driverValue
          ? parseFloat(freight.driverValue)
          : "") as unknown as number,
        advancePercent: parseFloat(freight.advancePercent),
        notes: freight.notes ?? "",
      });
    }
  }, [freight, reset]);

  async function onSubmit(data: FreightFormData) {
    try {
      await mutation.mutateAsync(data);
      toast.success(isEdit ? "Frete atualizado!" : "Frete cadastrado!");
      navigate("/freights");
    } catch (err: unknown) {
      const msg = (
        err as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      toast.error(msg ?? "Erro ao salvar frete");
    }
  }

  if (isEdit && loadingFreight) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => navigate("/freights")}
        >
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Editar frete" : "Novo frete"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? `${freight?.originCity}/${freight?.originState} → ${freight?.destinationCity}/${freight?.destinationState}`
              : "Preencha os dados do frete"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Empresa */}
        <FormSection title="Empresa">
          <div className="space-y-1.5">
            <Label>Empresa embarcadora *</Label>
            <ComboboxSelect
              options={companyOptions}
              value={(companyField.value as string) ?? ""}
              onChange={companyField.onChange}
              placeholder="Selecione a empresa..."
              searchPlaceholder="Buscar empresa..."
              aria-invalid={!!companyError}
            />
            {companyError && (
              <p className="text-xs text-destructive">{companyError.message}</p>
            )}
          </div>
        </FormSection>

        {/* Rota */}
        <FormSection title="Rota">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="originCity">Cidade de origem *</Label>
              <Input
                id="originCity"
                aria-invalid={!!errors.originCity}
                {...register("originCity")}
              />
              {errors.originCity && (
                <p className="text-xs text-destructive">
                  {errors.originCity.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="originState">UF origem *</Label>
              <select
                id="originState"
                aria-invalid={!!errors.originState}
                {...register("originState")}
                className={cn(
                  selectClass,
                  errors.originState && "border-destructive",
                )}
              >
                <option value="">Selecione…</option>
                {UF_LIST.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
              {errors.originState && (
                <p className="text-xs text-destructive">
                  {errors.originState.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="destinationCity">Cidade de destino *</Label>
              <Input
                id="destinationCity"
                aria-invalid={!!errors.destinationCity}
                {...register("destinationCity")}
              />
              {errors.destinationCity && (
                <p className="text-xs text-destructive">
                  {errors.destinationCity.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="destinationState">UF destino *</Label>
              <select
                id="destinationState"
                aria-invalid={!!errors.destinationState}
                {...register("destinationState")}
                className={cn(
                  selectClass,
                  errors.destinationState && "border-destructive",
                )}
              >
                <option value="">Selecione…</option>
                {UF_LIST.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
              {errors.destinationState && (
                <p className="text-xs text-destructive">
                  {errors.destinationState.message}
                </p>
              )}
            </div>
          </div>
        </FormSection>

        {/* Carga */}
        <FormSection title="Carga">
          <div className="space-y-1.5">
            <Label htmlFor="cargoDescription">Descrição da carga *</Label>
            <Input
              id="cargoDescription"
              aria-invalid={!!errors.cargoDescription}
              {...register("cargoDescription")}
            />
            {errors.cargoDescription && (
              <p className="text-xs text-destructive">
                {errors.cargoDescription.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cargoType">Tipo de carga</Label>
              <Input id="cargoType" {...register("cargoType")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cargoWeight">Peso (kg)</Label>
              <Input
                id="cargoWeight"
                type="number"
                step="0.01"
                min="0"
                {...register("cargoWeight")}
              />
            </div>
          </div>
        </FormSection>

        {/* Documentos fiscais */}
        <FormSection title="Documentos fiscais">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cteNumber">Número do CT-e</Label>
              <Input
                id="cteNumber"
                aria-invalid={!!errors.cteNumber}
                {...register("cteNumber")}
              />
              {errors.cteNumber && (
                <p className="text-xs text-destructive">
                  {errors.cteNumber.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deliveryNfeNumber">Número da NF-e (entrega)</Label>
              <Input
                id="deliveryNfeNumber"
                aria-invalid={!!errors.deliveryNfeNumber}
                {...register("deliveryNfeNumber")}
              />
              {errors.deliveryNfeNumber && (
                <p className="text-xs text-destructive">
                  {errors.deliveryNfeNumber.message}
                </p>
              )}
            </div>
          </div>
        </FormSection>

        {/* Valores */}
        <FormSection title="Valores">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!isOperator && (
              <div className="space-y-1.5">
                <Label htmlFor="freightValue">Valor do frete</Label>
                <MoneyInput
                  id="freightValue"
                  error={!!errors.freightValue}
                  {...register("freightValue")}
                />
                {errors.freightValue && (
                  <p className="text-xs text-destructive">
                    {errors.freightValue.message}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="driverValue">Valor do motorista</Label>
              <MoneyInput id="driverValue" {...register("driverValue")} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="advancePercent">% de adiantamento</Label>
            <div className="relative">
              <Input
                id="advancePercent"
                type="number"
                step="1"
                min="0"
                max="100"
                className="pr-8"
                {...register("advancePercent")}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none">
                %
              </span>
            </div>
          </div>

          {!isOperator && (
            <MarginDisplay
              freightValue={freightValue ?? ""}
              driverValue={driverValue ?? ""}
            />
          )}
        </FormSection>

        {/* Atribuição */}
        <FormSection title="Atribuição (opcional)">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Motorista</Label>
              <ComboboxSelect
                options={driverOptions}
                value={(driverField.value as string) ?? ""}
                onChange={driverField.onChange}
                placeholder="Selecione o motorista..."
                searchPlaceholder="Buscar motorista..."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Veículo</Label>
              <ComboboxSelect
                options={vehicleOptions}
                value={(vehicleField.value as string) ?? ""}
                onChange={vehicleField.onChange}
                placeholder="Selecione o veículo..."
                searchPlaceholder="Buscar veículo..."
              />
            </div>
          </div>
        </FormSection>

        {/* Observações */}
        <FormSection title="Observações">
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notas</Label>
            <textarea
              id="notes"
              rows={3}
              className={cn(
                "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "resize-none",
              )}
              {...register("notes")}
            />
          </div>
        </FormSection>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/freights")}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Salvando…"
          >
            {isEdit ? "Salvar alterações" : "Cadastrar frete"}
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}
